import { useEffect } from "react";

/**
 * This hook handles situations where you need to do something when clicking outside one or more elements.
 * Examples are:
 * * Closing a modal when clicking outside it.
 * * Closing a dropdown when clicking outside the open dropdown and not clicking the button to open the dropdown.
 *
 * The hook takes two arguments. The first is a function to call when the user clicks outside the defined elements.
 * this function will typically set a state to false: () => setOpenModal(false)
 *
 * The second is an array of elements considered "inside the component". For a dropdown, you would have a useRef reference
 * for both the open dropdown box and for the button that opens it. If the dropdown is open and the user clicks anything
 * besides the button to open it and the open dropdown, then the dropdown should close.
 */
export default (
  funcWhenClickOutside: () => void,
  insideElementRefs: React.RefObject<HTMLElement | null>[]
) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleClickOutside = (event: MouseEvent) => {
      const clickInside = insideElementRefs.some((ref) => {
        const el = ref.current;
        return el && el.contains(event.target as Node);
      });

      if (!clickInside) funcWhenClickOutside();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [funcWhenClickOutside, insideElementRefs]);
};
